import {
  Body,
  Box,
  Heading,
  Icon,
  Input,
  Label,
  Link,
  Stack,
  VStack,
  system,
} from '@hover/blueprint';
import type { BodyProps, BoxProps, StackProps } from '@hover/blueprint';
import { iconImages } from '@hover/icons';
import { includes } from 'ramda';
import React, { useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks/useHomeownerAppContext';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';
import { MIME_TYPES } from 'src/shared/utils/photoMimeTypes';

import { OriginalImageStatusMap } from '../../static_values';

type HTMLInputEventTarget = HTMLInputElement & EventTarget;

type UploadPhotoSheetProps = StackProps & {
  callback?: () => void;
  setImageFileToUpload: (file?: File | undefined) => void;
  imageInputContainerProps?: BoxProps;
  errorStateProps?: BoxProps;
};

type UploadPhotoInputProps = Pick<
  UploadPhotoSheetProps,
  | 'callback'
  | 'setImageFileToUpload'
  | 'imageInputContainerProps'
  | 'errorStateProps'
> & {
  bodySize?: BodyProps['size'];
  bodyColor?: BodyProps['color'];
};

const UploadPhotoInput = ({
  callback,
  setImageFileToUpload,
  bodySize = 400,
  bodyColor = 'neutral.900',
  imageInputContainerProps,
  errorStateProps,
}: UploadPhotoInputProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [invalidUpload, setInvalidUpload] = useState<false | string>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const { trackingData } = useHomeownerAppContext();
  const t = useTranslate([
    TranslationNamespace.HOMEOWNER_APP,
    TranslationNamespace.COMMON,
  ]);

  const location = useLocation();
  const history = useHistory();

  const uploadStatusDescription = (
    location.state as {
      uploadErrorMessage?: keyof typeof OriginalImageStatusMap;
    }
  )?.uploadErrorMessage;

  const uploadedStatusKey =
    uploadStatusDescription && OriginalImageStatusMap[uploadStatusDescription]
      ? OriginalImageStatusMap[uploadStatusDescription]
      : OriginalImageStatusMap.default;

  const originalUploadErrorMessage =
    uploadStatusDescription && uploadedStatusKey && t(uploadedStatusKey);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<
    string | undefined
  >(originalUploadErrorMessage);

  const onUploadPhotoChange: (
    event: React.FormEvent<HTMLInputElement>,
  ) => void = event => {
    setInvalidUpload(false);
    const inputTarget = event.target as HTMLInputEventTarget;
    const photoUploadFile = inputTarget.files && inputTarget.files[0];

    if (!photoUploadFile || !includes(photoUploadFile.type, MIME_TYPES)) {
      setInvalidUpload(t('uploadPhotoSheet.invalidUploadMimeType'));
      return;
    }

    const fileSizeLimit = 1024 * 1024 * 25; // 25MB
    if (photoUploadFile && photoUploadFile.size > fileSizeLimit) {
      setInvalidUpload(t('uploadPhotoSheet.invalidUploadTooLarge'));
      return;
    }

    setImageFileToUpload(photoUploadFile);
    if (callback) {
      callback();
    }
  };

  const onInputDropOrClick = () => {
    buttonPressed({
      ...trackingData,
      track_id: 'drag-drop-upload',
      button_text: 'Drag and drop here or choose a photo',
      page_or_screen_name: Page.HOMEOWNER_APP_CUSTOMIZE,
      primary_cta: true,
    });
  };

  return (
    <>
      <Box display={{ tablet: 'none' }} paddingBottom={100} width="100%">
        <Button
          onClick={() => {
            inputRef.current?.click();
          }}
          optionalTrackingProperties={{
            ...trackingData,
            track_id: 'photo-library',
            button_location: 'add-photo-bottom-sheet',
          }}
          size="large"
          textDecoration="none"
          width="100%"
        >
          {t('uploadPhotoSheet.addPhoto')}
        </Button>
      </Box>
      <Box
        alignItems="center"
        bg={isDragging ? 'darkAlpha.50' : 'transparent'}
        borderColor="neutral.550"
        borderRadius="300"
        borderStyle="solid"
        borderWidth={400}
        display={{ base: 'none', tablet: 'flex' }}
        flex={{ tablet: 1 }}
        flexDirection="column"
        height="750"
        justifyContent="center"
        maxWidth="662px"
        position="relative"
        {...imageInputContainerProps}
      >
        {(invalidUpload || uploadErrorMessage) && (
          <Box
            bg="darkAlpha.100"
            borderRadius="300"
            justifyContent="space-between"
            padding={400}
            position="absolute"
            top={400}
            width="95%"
            zIndex={1}
            {...errorStateProps}
          >
            <Box flexDirection="column">
              <Body color="danger.600" margin={0} size={bodySize}>
                {invalidUpload || uploadErrorMessage}
              </Body>
            </Box>

            <Body
              color={bodyColor}
              cursor="pointer"
              margin={0}
              marginLeft={100}
              onClick={() => {
                setInvalidUpload(false);
                setIsDragging(false);
                setUploadErrorMessage(undefined);
                history.replace({
                  pathname: location.pathname,
                  state: undefined,
                });
              }}
              size={bodySize}
              textDecoration="underline"
              whiteSpace="nowrap"
            >
              {t('uploadPhotoSheet.invalidConfirm')}
            </Body>
          </Box>
        )}

        {isDragging ? (
          <Body color={bodyColor} size={bodySize}>
            {t('uploadPhotoSheet.onDragEnterText')}
          </Body>
        ) : (
          <>
            <Icon color={bodyColor} icon={iconImages} size="small" />
            <Label
              color={bodyColor}
              fontWeight={700}
              htmlFor="homeowner-upload-photo"
              size={bodySize}
              textAlign="center"
              width="100%"
            >
              <Trans
                components={{
                  LineBreak: <system.br display="initial" />,
                }}
                i18nKey="uploadPhotoSheet.dragAndDrop"
                t={t}
              />
              <Link
                as="span"
                color={bodyColor}
                display="inline"
                fontWeight={400}
                textDecoration="underline"
              >
                {t('uploadPhotoSheet.desktopAction')}
              </Link>
            </Label>
          </>
        )}

        {/* MIME_TYPES should match hyperion app/models/ai_original_image.rb VALID_IMAGE_MIME_TYPES */}
        <Input
          accept={MIME_TYPES.join(',')}
          cursor="pointer"
          height="100%"
          id="homeowner-upload-photo"
          onChange={onUploadPhotoChange}
          onClick={onInputDropOrClick}
          onDragEnter={event => {
            event.preventDefault();
            event.stopPropagation();
            setInvalidUpload(false);
            setIsDragging(true);
          }}
          onDragLeave={() => {
            setIsDragging(false);
          }}
          onDrop={onInputDropOrClick}
          opacity="0"
          position="absolute"
          ref={inputRef}
          type="file"
          width="100%"
        />
      </Box>
    </>
  );
};

const UploadPhotoSheet: React.FC<UploadPhotoSheetProps> = ({
  callback,
  setImageFileToUpload,
  imageInputContainerProps,
  errorStateProps,
  ...props
}) => {
  const t = useTranslate([
    TranslationNamespace.HOMEOWNER_APP,
    TranslationNamespace.COMMON,
  ]);

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      justifyContent={{ base: 'space-around', tablet: 'space-between' }}
      spacing={{ base: 300, tablet: 500, width350: 1000 }}
      {...props}
    >
      <VStack alignItems="flex-start" flex={{ tablet: 1 }}>
        <Heading marginBottom="0 !important" size={{ base: 700, tablet: 800 }}>
          {t('uploadPhotoSheet.header')}
        </Heading>
        <Body
          color="text.secondary"
          maxWidth={{ tablet: '550px' }}
          paddingBottom={{ base: 900, tablet: 0 }}
          pt={200}
        >
          <Trans
            components={{
              LineBreak: <system.br display="initial" />,
            }}
            i18nKey="uploadPhotoSheet.highResolutionPhotos"
            t={t}
          />
        </Body>
      </VStack>
      <UploadPhotoInput
        callback={callback}
        setImageFileToUpload={setImageFileToUpload}
        {...imageInputContainerProps}
        {...errorStateProps}
      />
    </Stack>
  );
};

export { UploadPhotoSheet, UploadPhotoInput };
